import React, { forwardRef, cloneElement } from "react"
import PropTypes from "prop-types"
import { useMergeRefs } from "@floating-ui/react"

import { FocusManager } from "./focus-manager.js"
import { usePopover } from "./usePopover.js"
import { BasePopover } from "./base-popover.js"

export const Popover = forwardRef(
  (
    {
      children,
      target,
      open = false,
      onOpenChange = null,
      placement = "bottom",
      size = "m",
      bezel = true,
      focusOrder = ["content"],
      initialFocus = 0,
      outOfFlow = false,
      flip = true,
      shift = true,
      focusTrap = true,
      lowOffset = false,
      mediumOffset = false,
      highOffset = false,
      noOffset = false,
      stretch = false,
      returnFocus = true,
      ...props
    },
    ref
  ) => {
    // filtering out style and className
    /* eslint-disable-next-line react/prop-types */
    const { style, className, ...rest } = props

    const { getTargetProps, getPopoverProps, getFocusProps } = usePopover({
      open,
      placement,
      outOfFlow,
      size,
      bezel,
      flip,
      focusOrder,
      returnFocus,
      initialFocus,
      focusTrap,
      noOffset,
      lowOffset,
      mediumOffset,
      highOffset,
      onOpenChange,
      shift,
      stretch,
      ref,
    })

    const popoverRef = useMergeRefs([ref, getPopoverProps().ref])

    return (
      <>
        {cloneElement(target, {
          ...getTargetProps(),
        })}
        {open && (
          <FocusManager {...getFocusProps()}>
            <BasePopover {...rest} {...getPopoverProps()} ref={popoverRef}>
              {children}
            </BasePopover>
          </FocusManager>
        )}
      </>
    )
  }
)

Popover.displayName = "Popover"

Popover.propTypes = {
  /**
   * React node to render inside Popover.
   */
  children: PropTypes.node.isRequired,
  /**
   * Target node that triggers the Popover.
   */
  target: PropTypes.node.isRequired,
  /**
   * Decides the open state for Popover.
   */
  open: PropTypes.bool,
  /**
   * Size of the Popover.
   */
  size: PropTypes.oneOf(["s", "m", "l"]),
  /**
   * Adds padding on all sides.
   */
  bezel: PropTypes.bool,
  /**
   * Placement of the Popover with respect to target node.
   *
   * Popover supports multiple placements:
   *
   * * `auto` • `auto-start` • `auto-end`
   * * `top` • `top-start` • `top-end`
   * * `bottom` • `bottom-start` • `bottom-end`
   * * `left` • `left-start` • `left-end`
   * * `right` • `right-start` • `right-end`
   */
  placement: PropTypes.oneOf([
    "auto",
    "auto-start",
    "auto-end",
    "top",
    "top-start",
    "top-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "right",
    "right-start",
    "right-end",
    "left",
    "left-start",
    "left-end",
  ]),
  /**
   * The order in which focus cycles.
   *
   * * `reference` - Target element which acts as pivot for Popover.
   * * `floating` - Popover wrapper element.
   * * `content` - Content inside Popover wrapper.
   *
   * By default it's value is ['content'] - Subsequent tabs will cycle through the tabbable contents of the Popover content.
   *
   * If the value is set to ['floating', 'content'] - Includes floating element in focus cycle.
   *
   * If the value is set to ['reference', 'content'] - Includes reference element in focus cycle.
   *
   * * If the value is set to ['reference', 'floating', 'content'] - tabs will cycle through the reference, floating & tabbable contents of the Popover content.
   *
   */
  focusOrder: PropTypes.arrayOf(
    PropTypes.oneOf(["reference", "floating", "content"])
  ),
  /**
   * Element to initially focus when Popover is opened. Can be either a number or a ref.
   * Number is decided by the tabbable index as specified by the focusOrder.
   */
  initialFocus: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.number,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  /**
   * When Popover is added to a container with `position: fixed` and/or `overflow: hidden`,
   * it creates a containing block with a stacking context which the Popover can't skip.
   * In such scenarios, enabling `outOfFlow` will remove Popover from its parent's flow.
   */
  outOfFlow: PropTypes.bool,
  /**
   * Flips the position of the floating element if there is space constraint
   * in order to make it visible in viewport.
   */
  flip: PropTypes.bool,
  /**
   * Enables the Popover to stretch and fill the width of its container. By default, the Popover matches the width of its content.
   *
   * Strech works only in a relatively positioned container. Refer to the example below.
   */
  stretch: PropTypes.bool,
  /**
   * Shifts the Popover horizontally to avoid the content from overflowing the viewport.
   */
  shift: PropTypes.bool,
  /**
   * Add focus trap in the content, outside content cannot be accessed if enabled.
   */
  focusTrap: PropTypes.bool,
  /**
   * adds a low spacing between target and Popover.
   */
  lowOffset: PropTypes.bool,
  /**
   * adds a medium spacing between target and Popover.
   */
  mediumOffset: PropTypes.bool,
  /**
   * adds a high spacing between target and Popover.
   */
  highOffset: PropTypes.bool,
  /**
   * removes spacing between target and Popover.
   */
  noOffset: PropTypes.bool,
  /**
   * callback when open state is changed.
   */
  onOpenChange: PropTypes.func,
  /**
   * determines if focus should be returned to the reference element.
   */
  returnFocus: PropTypes.bool,
}

Popover.defaultProps = {
  open: false,
  size: "m",
  bezel: true,
  placement: "bottom",
  focusOrder: ["content"],
  initialFocus: 0,
  outOfFlow: false,
  flip: true,
  shift: true,
  stretch: false,
  focusTrap: true,
  lowOffset: false,
  mediumOffset: false,
  highOffset: false,
  noOffset: false,
  onOpenChange: null,
  returnFocus: true,
}
